import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Plus, Edit2, Trash2, Search, Download, Upload, X, 
  Cpu, Phone, DollarSign, Pause, Play, ChevronDown, Users, Archive, RotateCcw, AlertTriangle, Wallet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

// Format a backend "YYYY-MM" string into a readable label, e.g. "April 2026".
const formatYearMonth = (ym) => {
  if (!ym || typeof ym !== "string" || ym.length < 7) return null;
  const year = parseInt(ym.slice(0, 4));
  const month = parseInt(ym.slice(5, 7));
  if (!year || !month) return null;
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// Live "what if" estimate while typing in the modal. The resume month is the first
// month NOT yet covered plus N prepaid months. For a brand-new customer that base is
// the current month. For an existing customer the current month may already be paid
// (the auto-biller settles it on the 1st), so we anchor off the backend-computed
// next_payment_month: base = next_payment_month - stored_prepaid = first unpaid month.
const estimateResumeLabel = (prepaidMonths, customer) => {
  const n = parseInt(prepaidMonths) || 0;
  if (n <= 0) return null;
  // Base = index of the first month not yet covered (months counted as year*12 + (month-1)).
  let baseIndex;
  if (customer && customer.next_payment_month && (customer.prepaid_months || 0) > 0) {
    const y = parseInt(customer.next_payment_month.slice(0, 4));
    const mo = parseInt(customer.next_payment_month.slice(5, 7));
    baseIndex = (y * 12 + (mo - 1)) - (customer.prepaid_months || 0);
  } else {
    const d = new Date();
    baseIndex = d.getFullYear() * 12 + d.getMonth();
  }
  const total = baseIndex + n;
  const d2 = new Date(Math.floor(total / 12), total % 12, 1);
  return d2.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  // group index -> chosen "keep" customer id
  const [duplicateKeepChoice, setDuplicateKeepChoice] = useState({});
  // customer_id -> true if they have a portal login (Machines & Repairs -> Accounts)
  const [customerIdsWithAccount, setCustomerIdsWithAccount] = useState(new Set());
  const navigate = useNavigate();
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [prepaidOnly, setPrepaidOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingMachine, setEditingMachine] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    machines: [],
    total_cost: 0,
    status: "active",
    prepaid_months: 0,
    notes: ""
  });

  // Machine form state
  const [machineForm, setMachineForm] = useState({
    name: "",
    monthly_fee: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, machinesRes, archivedRes, duplicatesRes, accountsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/machine-types`),
        axios.get(`${API}/customers/archived`).catch(() => ({ data: [] })),
        axios.get(`${API}/customers/duplicates`).catch(() => ({ data: [] })),
        axios.get(`${API}/customer-accounts`).catch(() => ({ data: [] })),
      ]);
      const _sortByName = (arr) => (arr || []).slice().sort((a,b) => (a.name||'').localeCompare(b.name||'', undefined, {sensitivity:'base'}));
      setCustomers(_sortByName(customersRes.data));
      setMachineTypes(machinesRes.data);
      setArchivedCustomers(_sortByName(archivedRes.data));
      setDuplicateGroups(duplicatesRes.data || []);
      setCustomerIdsWithAccount(new Set((accountsRes.data || []).map((a) => a.customer_id)));
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // ---- Duplicate customers (same phone, two records — the "Touti" bug) ----
  const handleMergeDuplicates = async (group, keepId) => {
    const removeId = group.customers.find((c) => c.id !== keepId)?.id;
    if (!keepId || !removeId) {
      toast.error("Pick which record to keep first");
      return;
    }
    const keepName = group.customers.find((c) => c.id === keepId)?.name;
    const removeName = group.customers.find((c) => c.id === removeId)?.name;
    if (!window.confirm(
      `Merge "${removeName}" into "${keepName}"?\n\nMachines, billing, payment history and login will be combined onto "${keepName}". "${removeName}" will be archived (not deleted — you can still find it under Archived).`
    )) return;
    try {
      const res = await axios.post(`${API}/customers/merge`, {
        keep_customer_id: keepId,
        remove_customer_id: removeId,
      });
      toast.success(`Merged into "${res.data.kept_customer_name}" — ${res.data.machines_combined} machine(s), $${res.data.total_fee}/mo`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
  };

  const handleRestoreCustomer = async (customer) => {
    try {
      await axios.post(`${API}/customers/${customer.id}/restore`);
      toast.success(`${customer.name} restored`);
      fetchData();
    } catch (e) {
      toast.error("Failed to restore customer");
    }
  };

  const handleHardDeleteCustomer = async (customer) => {
    const typed = window.prompt(
      `⚠️ PERMANENT DELETE\n\nThis will permanently delete "${customer.name}" AND wipe their payment history. This cannot be undone.\n\nType the customer name exactly to confirm:`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== customer.name.trim().toLowerCase()) {
      toast.error("Name didn't match — deletion cancelled");
      return;
    }
    try {
      await axios.delete(`${API}/customers/${customer.id}?hard=true`);
      toast.success("Customer permanently deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete customer");
    }
  };

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setForm({
      name: "",
      phone: "",
      email: "",
      machines: [],
      total_cost: 0,
      status: "active",
      prepaid_months: 0,
      notes: ""
    });
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name,
      phone: customer.phone || "",
      email: customer.email || "",
      machines: customer.machines || [],
      total_cost: customer.total_cost || 0,
      status: customer.status || "active",
      prepaid_months: customer.prepaid_months || 0,
      notes: customer.notes || ""
    });
    setShowModal(true);
  };

  const handleSaveCustomer = async () => {
    if (!form.name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, form);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, form);
        toast.success("Customer added");
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to save customer");
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(
      `Archive ${customer.name}?\n\nThe customer will be hidden from your list, but their payment history will be preserved and you can restore them anytime from the "Archived" section.`
    )) return;

    try {
      await axios.delete(`${API}/customers/${customer.id}`);
      toast.success(`${customer.name} archived — can be restored anytime`);
      fetchData();
    } catch (e) {
      toast.error("Failed to archive customer");
    }
  };

  const handleToggleStatus = async (customer) => {
    const newStatus = customer.status === "active" ? "paused" : "active";
    try {
      await axios.put(`${API}/customers/${customer.id}`, { status: newStatus });
      toast.success(`Customer ${newStatus === "active" ? "activated" : "paused"}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  // Machine management
  const handleAddMachine = (machine) => {
    const existing = form.machines.find(m => m.machine_type_id === machine.id);
    if (existing) {
      setForm({
        ...form,
        machines: form.machines.map(m => 
          m.machine_type_id === machine.id 
            ? { ...m, quantity: m.quantity + 1 }
            : m
        )
      });
    } else {
      setForm({
        ...form,
        machines: [...form.machines, {
          machine_type_id: machine.id,
          machine_name: machine.name,
          quantity: 1
        }]
      });
    }
  };

  const handleRemoveMachine = (machineTypeId) => {
    setForm({
      ...form,
      machines: form.machines.filter(m => m.machine_type_id !== machineTypeId)
    });
  };

  const handleUpdateMachineQty = (machineTypeId, qty) => {
    if (qty < 1) {
      handleRemoveMachine(machineTypeId);
      return;
    }
    setForm({
      ...form,
      machines: form.machines.map(m => 
        m.machine_type_id === machineTypeId 
          ? { ...m, quantity: qty }
          : m
      )
    });
  };

  // Machine type management
  const handleAddMachineType = () => {
    setEditingMachine(null);
    setMachineForm({ name: "", monthly_fee: 0 });
    setShowMachineModal(true);
  };

  const handleEditMachineType = (machine) => {
    setEditingMachine(machine);
    setMachineForm({ name: machine.name, monthly_fee: machine.monthly_fee });
    setShowMachineModal(true);
  };

  const handleSaveMachineType = async () => {
    if (!machineForm.name.trim()) {
      toast.error("Machine name is required");
      return;
    }

    try {
      if (editingMachine) {
        await axios.put(`${API}/machine-types/${editingMachine.id}`, machineForm);
        toast.success("Machine type updated");
      } else {
        await axios.post(`${API}/machine-types`, machineForm);
        toast.success("Machine type added");
      }
      setShowMachineModal(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to save machine type");
    }
  };

  const handleDeleteMachineType = async (machine) => {
    if (!window.confirm(`Delete ${machine.name}?`)) return;
    
    try {
      await axios.delete(`${API}/machine-types/${machine.id}`);
      toast.success("Machine type deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete machine type");
    }
  };

  // Export/Import
  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export complete");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} customers`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      fetchData();
    } catch (e) {
      toast.error("Import failed");
    }
    
    e.target.value = '';
  };

  // Calculate total fee
  const calculateTotalFee = (machines) => {
    return machines.reduce((total, m) => {
      const machine = machineTypes.find(mt => mt.id === m.machine_type_id);
      return total + (machine?.monthly_fee || 0) * m.quantity;
    }, 0);
  };

  // Filter customers
  const prepaidCount = customers.filter(c => (c.prepaid_months || 0) > 0).length;
  const filteredCustomers = customers
    .filter(c => !prepaidOnly || (c.prepaid_months || 0) > 0)
    .filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone?.includes(searchQuery)
    )
    // When showing prepaid only, order by who pays soonest so the ones coming
    // due are at the top instead of buried in alphabetical order.
    .sort((a, b) => {
      if (!prepaidOnly) return 0;
      return (a.next_payment_month || "9999-99").localeCompare(b.next_payment_month || "9999-99");
    });

  if (loading) {
    return (
      <div className="space-y-4 animate-fadeIn">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="skeleton h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Customers</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">{customers.length} customers registered</p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <Button
            onClick={handleAddMachineType}
            variant="outline"
            className="border-[#27272A] hover:bg-[#1A1A1A] w-full sm:w-auto"
            data-testid="add-machine-type-btn"
          >
            <Cpu size={16} className="mr-2" />
            <span className="truncate">Manage Machines</span>
          </Button>
          <label className="cursor-pointer w-full sm:w-auto">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              className="hidden"
              data-testid="import-input"
            />
            <Button variant="outline" className="border-[#27272A] hover:bg-[#1A1A1A] w-full sm:w-auto" asChild>
              <span>
                <Upload size={16} className="mr-2" />
                Import
              </span>
            </Button>
          </label>
          <Button
            onClick={handleExport}
            variant="outline"
            className="border-[#27272A] hover:bg-[#1A1A1A] w-full sm:w-auto"
            data-testid="export-btn"
          >
            <Download size={16} className="mr-2" />
            Export
          </Button>
          <Button
            onClick={() => setShowArchived(s => !s)}
            variant="outline"
            className="border-[#27272A] hover:bg-[#1A1A1A] w-full sm:w-auto"
            data-testid="toggle-archived-btn"
          >
            <Archive size={16} className="mr-2" />
            <span className="truncate">{showArchived ? "Hide" : "Archived"}{archivedCustomers.length > 0 ? ` (${archivedCustomers.length})` : ""}</span>
          </Button>
          <Button
            onClick={handleAddCustomer}
            className="bg-[#00E054] text-black hover:bg-[#00E054]/90 w-full sm:w-auto col-span-2 sm:col-span-1"
            data-testid="add-customer-btn"
          >
            <Plus size={16} className="mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Possible duplicate customers (same phone, two records) */}
      {duplicateGroups.length > 0 && (
        <div className="card p-4 border-orange-500/30 bg-orange-500/5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="text-orange-400" size={16} />
            <h3 className="text-sm font-medium text-orange-400">
              Possible Duplicate Customers ({duplicateGroups.length})
            </h3>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            These share the same phone number — often from a portal signup approved as "new customer"
            when the customer already existed. Merging combines machines, billing and login onto one record.
          </p>
          <div className="space-y-3">
            {duplicateGroups.map((group, gi) => (
              <div key={group.phone_digits} className="p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                <p className="text-gray-500 text-xs mb-2">Phone: {group.customers[0]?.phone || group.phone_digits}</p>
                <div className="space-y-1 mb-3">
                  {group.customers.map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <span className="text-white font-medium">{c.name}</span>
                      <span className="text-gray-500">{c.machines_count} machine(s)</span>
                      <span className="text-gray-500">${c.total_fee}/mo</span>
                      {c.has_account ? (
                        <span className="px-1.5 py-0.5 rounded bg-[#00E054]/15 text-[#00E054] text-xs">has login</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-gray-700/40 text-gray-400 text-xs">no login</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={duplicateKeepChoice[gi] || ""}
                    onChange={(e) => setDuplicateKeepChoice({ ...duplicateKeepChoice, [gi]: e.target.value })}
                    className="flex-1 min-w-[200px] px-3 py-2 bg-[#050505] border border-[#27272A] rounded-lg text-white text-xs focus:outline-none focus:border-orange-400/50"
                    data-testid={`duplicate-keep-select-${gi}`}
                  >
                    <option value="">Keep which one?</option>
                    {group.customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        Keep "{c.name}" ({c.machines_count} machine(s), {c.has_account ? "has login" : "no login"})
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => handleMergeDuplicates(group, duplicateKeepChoice[gi])}
                    disabled={!duplicateKeepChoice[gi]}
                    className="bg-orange-500 text-black text-xs font-bold hover:bg-orange-500/90 disabled:opacity-50"
                    data-testid={`merge-duplicates-${gi}`}
                  >
                    Merge
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archived Customers Panel */}
      {showArchived && (
        <div className="card p-4 border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-yellow-400 flex items-center gap-2">
              <Archive size={16} />
              Archived Customers ({archivedCustomers.length})
            </h3>
            <p className="text-xs text-gray-500">Restore brings back the customer with all payment history intact</p>
          </div>
          {archivedCustomers.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No archived customers</p>
          ) : (
            <div className="space-y-2">
              {archivedCustomers.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{c.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {c.phone || "no phone"} · ${c.total_fee || 0}/mo
                      {c.archived_at ? ` · archived ${new Date(c.archived_at).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleRestoreCustomer(c)}
                    size="sm"
                    className="bg-[#00E054] text-black hover:bg-[#00E054]/90"
                    data-testid={`restore-${c.id}`}
                  >
                    <RotateCcw size={14} className="mr-1" />
                    Restore
                  </Button>
                  <Button
                    onClick={() => handleHardDeleteCustomer(c)}
                    size="sm"
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`hard-delete-${c.id}`}
                    title="Permanently delete (cannot be undone)"
                  >
                    <AlertTriangle size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Machine Types Section */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-gray-400 mb-3">Machine Types & Fees</h3>
        <div className="flex flex-wrap gap-2">
          {machineTypes.map((mt) => (
            <div 
              key={mt.id} 
              className="machine-badge group cursor-pointer hover:border-[#00E054]"
              onClick={() => handleEditMachineType(mt)}
            >
              <span className="text-white">{mt.name}</span>
              <span className="text-[#00E054]">${mt.monthly_fee}</span>
            </div>
          ))}
          <button 
            onClick={handleAddMachineType}
            className="machine-badge hover:border-[#00E054] hover:text-[#00E054]"
          >
            <Plus size={14} />
            <span>Add Type</span>
          </button>
        </div>
      </div>

      {/* Search + prepaid filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white"
            data-testid="search-input"
          />
        </div>
        <button
          onClick={() => setPrepaidOnly(v => !v)}
          data-testid="prepaid-filter"
          className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors whitespace-nowrap ${
            prepaidOnly
              ? "bg-[#00C2FF]/10 text-[#00C2FF] border-[#00C2FF]/40"
              : "bg-[#0A0A0A] text-gray-300 border-[#27272A] hover:border-[#00C2FF]/40"
          }`}
        >
          <Wallet size={16} />
          {prepaidOnly ? "Showing prepaid" : "Prepaid only"}
          <span className={`px-1.5 rounded ${prepaidOnly ? "bg-[#00C2FF]/20" : "bg-gray-700/40"}`}>
            {prepaidCount}
          </span>
        </button>
      </div>

      {/* Customers List */}
      <div className="space-y-3">
        {filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <Users size={64} className="mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-white mb-2">No customers found</h3>
            <p className="text-gray-500">Add your first customer to get started</p>
          </div>
        ) : (
          filteredCustomers.map((customer, idx) => (
            <div 
              key={customer.id} 
              className={`card p-4 animate-fadeIn`}
              style={{ animationDelay: `${idx * 0.05}s` }}
              data-testid={`customer-card-${customer.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-white">{customer.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      customer.status === 'active' ? 'status-paid' : 'status-paused'
                    }`}>
                      {customer.status}
                    </span>
                    {customer.prepaid_months > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#00C2FF]/10 text-[#00C2FF] border border-[#00C2FF]/20">
                        {customer.prepaid_months}m prepaid{formatYearMonth(customer.next_payment_month) ? ` · pays again ${formatYearMonth(customer.next_payment_month)}` : ""}
                      </span>
                    )}
                    <button
                      onClick={() => navigate("/admin")}
                      title="Portal login lives in Machines & Repairs → Accounts — click to go there"
                      className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                        customerIdsWithAccount.has(customer.id)
                          ? "bg-[#00E054]/10 text-[#00E054] border-[#00E054]/20 hover:bg-[#00E054]/20"
                          : "bg-gray-700/20 text-gray-500 border-gray-600/30 hover:bg-gray-700/40"
                      }`}
                      data-testid={`account-status-${customer.id}`}
                    >
                      {customerIdsWithAccount.has(customer.id) ? "🔑 has portal login" : "no portal login"}
                    </button>
                  </div>
                  {customer.phone && (
                    <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                      <Phone size={14} />
                      {customer.phone}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {customer.machines?.map((m, i) => (
                      <span key={i} className="machine-badge">
                        {m.quantity}x {m.machine_name}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-6 flex-wrap">
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Your Cost</p>
                    <p className="text-lg font-mono text-[#EF4444]">${customer.total_cost}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Customer Fee</p>
                    <p className="text-lg font-mono text-[#00E054]">${customer.total_fee}</p>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleToggleStatus(customer)}
                      className="hover:bg-[#1A1A1A]"
                      data-testid={`toggle-status-${customer.id}`}
                    >
                      {customer.status === 'active' ? (
                        <Pause size={18} className="text-[#EAB308]" />
                      ) : (
                        <Play size={18} className="text-[#00E054]" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEditCustomer(customer)}
                      className="hover:bg-[#1A1A1A]"
                      data-testid={`edit-customer-${customer.id}`}
                    >
                      <Edit2 size={18} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteCustomer(customer)}
                      className="hover:bg-[#EF4444]/10 text-[#EF4444]"
                      data-testid={`delete-customer-${customer.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] mt-1"
                  placeholder="Customer name"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label>Phone (WhatsApp)</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] mt-1"
                  placeholder="+961 XX XXX XXX"
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Email <span className="text-gray-500 text-xs">(optional)</span></Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] mt-1"
                  placeholder="customer@example.com"
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label>Your Cost ($)</Label>
                <Input
                  type="number"
                  value={form.total_cost}
                  onChange={(e) => setForm({ ...form, total_cost: parseFloat(e.target.value) || 0 })}
                  className="bg-[#0A0A0A] border-[#27272A] mt-1"
                  data-testid="customer-cost-input"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select 
                  value={form.status} 
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] mt-1" data-testid="customer-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-[#27272A]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prepaid Months</Label>
                <Input
                  type="number"
                  value={form.prepaid_months}
                  onChange={(e) => setForm({ ...form, prepaid_months: parseInt(e.target.value) || 0 })}
                  className="bg-[#0A0A0A] border-[#27272A] mt-1"
                  min={0}
                  data-testid="customer-prepaid-input"
                />
                {form.prepaid_months > 0 && (
                  <p className="text-xs text-[#00C2FF] mt-1" data-testid="customer-prepaid-resume">
                    Starts paying again in {estimateResumeLabel(form.prepaid_months, editingCustomer)}
                  </p>
                )}
              </div>
            </div>

            {/* Machines */}
            <div>
              <Label>Machines</Label>
              <div className="mt-2 space-y-2">
                {form.machines.map((m) => (
                  <div key={m.machine_type_id} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                    <span className="flex-1 text-sm">{m.machine_name}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => handleUpdateMachineQty(m.machine_type_id, m.quantity - 1)}
                      >
                        -
                      </Button>
                      <span className="w-8 text-center font-mono">{m.quantity}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => handleUpdateMachineQty(m.machine_type_id, m.quantity + 1)}
                      >
                        +
                      </Button>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-[#EF4444]"
                      onClick={() => handleRemoveMachine(m.machine_type_id)}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full border-dashed border-[#27272A] hover:bg-[#1A1A1A]"
                      data-testid="add-machine-dropdown"
                    >
                      <Plus size={16} className="mr-2" />
                      Add Machine
                      <ChevronDown size={16} className="ml-auto" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0F0F0F] border-[#27272A]">
                    {machineTypes.map((mt) => (
                      <DropdownMenuItem
                        key={mt.id}
                        onClick={() => handleAddMachine(mt)}
                        className="cursor-pointer hover:bg-[#1A1A1A]"
                      >
                        <span>{mt.name}</span>
                        <span className="ml-auto text-[#00E054]">${mt.monthly_fee}/mo</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {form.machines.length > 0 && (
                <div className="mt-3 p-3 bg-[#00E054]/10 rounded-lg border border-[#00E054]/20">
                  <p className="text-sm text-gray-400">Calculated Monthly Fee</p>
                  <p className="text-2xl font-bold text-[#00E054]">
                    ${calculateTotalFee(form.machines)}
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full mt-1 p-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg text-white resize-none h-20"
                placeholder="Any notes..."
                data-testid="customer-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowModal(false)}
              className="border-[#27272A]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCustomer}
              className="bg-[#00E054] text-black hover:bg-[#00E054]/90"
              data-testid="save-customer-btn"
            >
              {editingCustomer ? "Update" : "Add"} Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Machine Type Modal */}
      <Dialog open={showMachineModal} onOpenChange={setShowMachineModal}>
        <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMachine ? "Edit Machine Type" : "Add Machine Type"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Machine Name *</Label>
              <Input
                value={machineForm.name}
                onChange={(e) => setMachineForm({ ...machineForm, name: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] mt-1"
                placeholder="e.g., L11, S19"
                data-testid="machine-name-input"
              />
            </div>
            <div>
              <Label>Monthly Hosting Fee ($)</Label>
              <Input
                type="number"
                value={machineForm.monthly_fee}
                onChange={(e) => setMachineForm({ ...machineForm, monthly_fee: parseFloat(e.target.value) || 0 })}
                className="bg-[#0A0A0A] border-[#27272A] mt-1"
                data-testid="machine-fee-input"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {editingMachine && (
              <Button 
                variant="destructive"
                onClick={() => {
                  handleDeleteMachineType(editingMachine);
                  setShowMachineModal(false);
                }}
                className="w-full sm:w-auto"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={() => setShowMachineModal(false)}
                className="border-[#27272A] flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveMachineType}
                className="bg-[#00E054] text-black hover:bg-[#00E054]/90 flex-1"
                data-testid="save-machine-btn"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
